import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Container, Typography, Box, Grid, Card, TextField, Button, Avatar } from '@mui/material';
import { useForm } from '@formspree/react';
import { RequestQuote } from '@mui/icons-material';
import Layout from '../components/Layout';

const glassStyle = {
  background: 'rgba(255, 255, 255, 0.25)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.18)',
  borderRadius: '16px',
  boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)'
};

const GetQuote = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', service: '', message: '', property_size: '', timeline: '' });
  const [state, handleSubmit] = useForm("xzzglnqq");

  return (
    <Layout>
      <Helmet>
        <title>Get Free Landscaping Quote Vancouver BC | Free Estimate West Vancouver | Green Mountain</title>
        <meta name="description" content="Get free landscaping quote Vancouver BC. Professional estimates for lawn care, garden design, hardscaping in West Vancouver & North Vancouver. Call (604) 202-0053" />
        <meta name="keywords" content="free landscaping quote vancouver, landscaping estimate west vancouver, lawn care quote north vancouver, garden design estimate bc, hardscaping quote vancouver" />
        <link rel="canonical" href="https://greenmountainlandscaping.ca/get-quote" />
      </Helmet>
      
      <Box
        sx={{
          minHeight: '40vh',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url("/images/348s (2).jpg")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            zIndex: -2
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(27, 94, 32, 0.8) 0%, rgba(46, 125, 50, 0.7) 100%)',
            zIndex: -1
          }
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ ...glassStyle, p: { xs: 3, md: 6 }, color: 'white', textAlign: 'center' }}>
            <Avatar sx={{ bgcolor: 'white', color: 'primary.main', width: 80, height: 80, mx: 'auto', mb: 3 }}>
              <RequestQuote sx={{ fontSize: 40 }} />
            </Avatar>
            <Typography variant="h1" component="h1" sx={{ mb: 3, textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>
              Get Free Landscaping Quote Vancouver BC
            </Typography>
            <Typography variant="h5" sx={{ mb: 4, opacity: 0.9 }}>
              Professional estimates for all landscaping services
            </Typography>
          </Box>
        </Container>
      </Box>
      
      <Container maxWidth="md" sx={{ py: 10 }}>
        <Typography variant="h1" component="h1" sx={{ mb: 4, color: 'primary.main', textAlign: 'center' }}>
          Get Free Landscaping Quote Vancouver BC
        </Typography>
        
        <Typography variant="body1" sx={{ mb: 6, fontSize: '1.1rem', textAlign: 'center' }}>
          Ready to transform your outdoor space? Get a <strong>free landscaping quote in Vancouver</strong> from Green Mountain Landscaping. We provide detailed estimates for all landscaping services in West Vancouver, North Vancouver, and Metro Vancouver.
        </Typography>

        <Card sx={{ ...glassStyle, p: 4 }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Full Name"
                  required
                  name="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  required
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  required
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Service Needed"
                  name="service"
                  value={formData.service}
                  onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                  SelectProps={{ native: true }}
                >
                  <option value="">Select Service</option>
                  <option value="landscape-design">Landscape Design</option>
                  <option value="lawn-care">Lawn Care & Maintenance</option>
                  <option value="garden-design">Garden Design</option>
                  <option value="hardscaping">Hardscaping & Patios</option>
                  <option value="irrigation">Irrigation Systems</option>
                  <option value="maintenance">Landscape Maintenance</option>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Property Size (sq ft)"
                  name="property_size"
                  value={formData.property_size}
                  onChange={(e) => setFormData({ ...formData, property_size: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Timeline"
                  name="timeline"
                  value={formData.timeline}
                  onChange={(e) => setFormData({ ...formData, timeline: e.target.value })}
                  placeholder="When do you want to start?"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Project Details"
                  placeholder="Describe your landscaping project, budget range, and any specific requirements..."
                  multiline
                  rows={4}
                  required
                  name="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={state.submitting}
                  sx={{ py: 2, fontSize: '1.1rem' }}
                >
                  {state.submitting ? 'Sending Request...' : 'Get My Free Quote'}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Card>

        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Or Call Us Directly</Typography>
          <Button variant="outlined" size="large" href="tel:+16042020053">
            (604) 202-0053
          </Button>
        </Box>
      </Container>
    </Layout>
  );
};

export default GetQuote;